/**
 * The signed-in two-step sign-in API (apiv2/dashboard_mfa.openapi.yaml, under /v2/edit/): the user's own factors
 * for the Sign-in security page, and the tenant-admin reset of a colleague's (P5).
 *
 * Every call passes `keepSessionOn403` (the McpConnectionsPage pattern): self-service refusals are 400/409, and a
 * 403 here (the reset's permission_required / session_mfa_required) is an answer, never an expired session. A 401
 * still signs out. GETs go through GET_ONE and POSTs through CREATE on a sub-path, so `keepSessionOn403` never
 * reaches a query string; `DELETE /mfa/` is the data provider's DELETE with `singleton`.
 *
 * Each call resolves to the response body and rejects with an {@link MfaApiError} for any status ≥ 400.
 */
import { CREATE, DELETE, GET_ONE } from '../api/dataProvider';
import { describeRefusal } from '../auth/mfaApi';

export const KEEP_SESSION = { keepSessionOn403: true };

// Keyed by the contract's operationIds: the data-provider call that reaches each. mfaContract.test.js holds every
// method and path to the spec.
export const SECURITY_CALLS = {
    dashboardMfaStatus: { type: GET_ONE, resource: 'mfa' },
    dashboardMfaDisable: { type: DELETE, resource: 'mfa' },
    dashboardMfaTotpStart: { type: CREATE, resource: 'mfa', urlSuffix: 'totp/' },
    dashboardMfaTotpConfirm: { type: CREATE, resource: 'mfa', urlSuffix: 'totp/confirm/' },
    dashboardMfaSmsStart: { type: CREATE, resource: 'mfa', urlSuffix: 'sms/' },
    dashboardMfaSmsConfirm: { type: CREATE, resource: 'mfa', urlSuffix: 'sms/confirm/' },
    dashboardMfaSmsChallenge: { type: CREATE, resource: 'mfa', urlSuffix: 'sms/challenge/' },
    dashboardMfaSetPrimary: { type: CREATE, resource: 'mfa', urlSuffix: 'primary/' },
    dashboardMfaRegenerateRecoveryCodes: { type: CREATE, resource: 'mfa', urlSuffix: 'recovery_codes/' },
    dashboardUserResetMfa: { type: CREATE, resource: 'user', urlSuffix: '{id}/reset_mfa/' },
};

const HTTP_METHOD = { [GET_ONE]: 'GET', [CREATE]: 'POST', [DELETE]: 'DELETE' };

// The method and contract path a call reaches.
export const httpOf = ({ type, resource, urlSuffix = '' }) => ({ method: HTTP_METHOD[type], path: `/${resource}/${urlSuffix}` });

export class MfaApiError extends Error {
    constructor(status, body) {
        super(describeRefusal(body, status === 0 ? 'Could not reach the server. Check your connection and try again.' : `The request failed (${status}). Try again.`));
        this.status = status;
        this.body = body;
        this.code = (body && typeof body.code === 'string' && body.code) || null;
    }
}

const unwrap = (res) => {
    const status = res?.statusCode ?? 0;
    if (status >= 400 || status === 0) {
        throw new MfaApiError(status, res?.data && !Array.isArray(res.data) ? res.data : null);
    }
    return res.data;
};

const request = (dataAPI, operationId, { data, id } = {}) => {
    const { type, resource, urlSuffix } = SECURITY_CALLS[operationId];
    const params = { ...KEEP_SESSION };
    if (urlSuffix) params.urlSuffix = urlSuffix.replace('{id}', String(id));
    if (type === DELETE) params.singleton = true;
    if (type !== GET_ONE) params.data = data || {};
    return dataAPI(type, resource, params).then(unwrap, () => {
        throw new MfaApiError(0, null);
    });
};

/** DashboardMfaStatus; 404 while the backend's feature flag is off. */
export const getMfaStatus = (dataAPI) => request(dataAPI, 'dashboardMfaStatus');

/** Step 1 of setting up or replacing a factor (needs the password): totp → the QR code, sms → a texted code. */
export const startFactor = (dataAPI, kind, { password, phone }) => (kind === 'sms'
    ? request(dataAPI, 'dashboardMfaSmsStart', { data: { password, phone } })
    : request(dataAPI, 'dashboardMfaTotpStart', { data: { password } }));

/** Step 2: the first code. Resolves DashboardMfaSessionRotated (a new token; codes on a first factor). */
export const confirmFactor = (dataAPI, kind, deviceId, code) =>
    request(dataAPI, kind === 'sms' ? 'dashboardMfaSmsConfirm' : 'dashboardMfaTotpConfirm', { data: { device_id: deviceId, code } });

/** Text a code to the user's phone (for regenerating codes or turning two-step sign-in off). */
export const sendSmsCode = (dataAPI, password) => request(dataAPI, 'dashboardMfaSmsChallenge', { data: { password } });

/** Resolves the updated DashboardMfaStatus. */
export const setPrimaryMethod = (dataAPI, method) => request(dataAPI, 'dashboardMfaSetPrimary', { data: { method } });

/** Resolves DashboardMfaRecoveryCodes. */
export const regenerateRecoveryCodes = (dataAPI, method, code) =>
    request(dataAPI, 'dashboardMfaRegenerateRecoveryCodes', { data: { method, code } });

/** Optional tenants only; ends every dashboard session of the user, this one included. */
export const disableMfa = (dataAPI, { password, method, code }) => request(dataAPI, 'dashboardMfaDisable', { data: { password, method, code } });

/** P5: a tenant admin resets a colleague's. Resolves DashboardUserMfaReset. */
export const resetColleagueMfa = (dataAPI, userId) => request(dataAPI, 'dashboardUserResetMfa', { id: userId });

/**
 * Refresh bootstrap.mfa (the user menu's "Sign-in security") from GET /mfa/: a session stored before the feature
 * (or its flag) has none, and a flag turned off leaves a stale one. 404 = the feature is off; any other failure
 * leaves what is stored.
 */
export const refreshMfaStatus = (dataAPI, setMfaStatus) => getMfaStatus(dataAPI).then(
    (status) => setMfaStatus(status),
    (err) => {
        if (err instanceof MfaApiError && err.status === 404) {
            setMfaStatus(null);
        }
    },
);
